import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { getSessionId } from "@/lib/flow";
import { track } from "@/lib/tracking";

const fmt = (ts) => new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
const WELCOME = "Ciao! Sono qui per aiutarti durante il tuo Beauty Test. Scrivimi pure, ti rispondo appena possibile.";

export const ChatWidget = () => {
  const { pathname } = useLocation();
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [text, setText] = useState("");
  const [name, setName] = useState(() => localStorage.getItem("fs_chat_name") || "");
  const [started, setStarted] = useState(() => localStorage.getItem("fs_chat_started") === "1");
  const endRef = useRef(null);

  useEffect(() => { api.get("/advisor/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!started) return;
    const load = () => api.get(`/chat/${getSessionId()}/messages`, { params: { peek: !open } })
      .then(({ data }) => { setMessages(data.messages); setUnread(open ? 0 : data.unread); }).catch(() => {});
    load();
    const t = setInterval(load, open ? 3000 : 8000);
    return () => clearInterval(t);
  }, [open, started]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  if (!config?.chat_enabled) return null;

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    localStorage.setItem("fs_chat_name", name);
    const { data } = await api.post(`/chat/${getSessionId()}/messages`, { text: t, name, page: pathname }).catch((e) => { toast.error(errMsg(e)); return { data: null }; });
    if (!data) return;
    setMessages((m) => [...m, data]);
    setText("");
    if (!started) { localStorage.setItem("fs_chat_started", "1"); setStarted(true); }
    track("chat_message_sent", { page: pathname });
  };

  const bottom = pathname === "/routine" ? "bottom-24" : "bottom-5";

  return (
    <>
      <button data-testid="chat-widget-toggle" aria-label="Chat con FineSkin" onClick={() => setOpen((o) => !o)}
        className={`fixed right-5 ${bottom} z-50 w-14 h-14 rounded-full bg-[#1A1A18] text-[#FAF8F5] shadow-[0_12px_30px_-10px_rgba(26,26,24,.6)] flex items-center justify-center transition-transform duration-200 hover:scale-105 active:scale-95`}>
        {open ? <X size={20} strokeWidth={1.5} /> : <MessageCircle size={22} strokeWidth={1.5} />}
        {!open && unread > 0 && (
          <span data-testid="chat-widget-unread" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#C5A059] text-[#1A1A18] text-[11px] font-medium flex items-center justify-center">{unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="chat-panel" role="dialog" aria-label="Chat live con la Beauty Advisor" className="fixed z-50 right-4 left-4 sm:left-auto sm:right-5 bottom-24 sm:w-[370px] h-[min(520px,calc(100vh-8rem))] bg-[#FAF8F5] border border-[#E6DFD5] shadow-[0_30px_60px_-20px_rgba(26,26,24,.35)] flex flex-col overflow-hidden fs-chat-in">
          <div className="bg-[#1A1A18] text-[#FAF8F5] px-5 py-4">
            <p className="text-[10px] tracking-[0.3em] uppercase text-white/50">Chat live</p>
            <p className="font-serif text-lg mt-1">La tua Beauty Advisor</p>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            <Bubble sender="admin" text={WELCOME} />
            {messages.map((m) => <Bubble key={m.id} sender={m.sender} text={m.text} ts={m.ts} />)}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="border-t border-[#E6DFD5] bg-white p-3 space-y-2">
            {!started && (
              <input data-testid="chat-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Il tuo nome (facoltativo)"
                className="w-full text-sm px-3 py-2 bg-[#FAF8F5] border border-[#E6DFD5] outline-none focus:border-[#C5A059]" />
            )}
            <div className="flex gap-2">
              <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio…"
                className="flex-1 text-sm px-3 py-2.5 bg-[#FAF8F5] border border-[#E6DFD5] outline-none focus:border-[#C5A059]" />
              <button data-testid="chat-send-btn" type="submit" disabled={!text.trim()} aria-label="Invia"
                className="w-11 h-11 bg-[#1A1A18] text-[#FAF8F5] flex items-center justify-center disabled:opacity-40 transition-colors hover:bg-[#2B2A27]">
                <Send size={16} strokeWidth={1.5} />
              </button>
            </div>
            {config.whatsapp_number && (
              <a data-testid="chat-whatsapp-link" href={`https://wa.me/${config.whatsapp_number.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                className="block text-center text-[11px] text-[#8C8275] hover:text-[#1A1A18] pt-1">Preferisci WhatsApp? Scrivici al {config.whatsapp_number}</a>
            )}
          </form>
        </div>
      )}
    </>
  );
};

const Bubble = ({ sender, text, ts }) => {
  const me = sender === "customer";
  return (
    <div className={`flex ${me ? "justify-end" : "justify-start"}`} data-testid={`chat-bubble-${sender}`}>
      <div className={`max-w-[82%] px-4 py-2.5 text-sm leading-relaxed ${me ? "bg-[#1A1A18] text-[#FAF8F5] rounded-2xl rounded-br-sm" : "bg-white border border-[#E6DFD5] text-[#1A1A18] rounded-2xl rounded-bl-sm"}`}>
        <p className="whitespace-pre-wrap break-words">{text}</p>
        {ts && <p className={`text-[10px] mt-1 ${me ? "text-white/50" : "text-[#8C8275]"}`}>{fmt(ts)}</p>}
      </div>
    </div>
  );
};
